import { DataGridPro, GridColumns, GridToolbar } from '@mui/x-data-grid-pro';
import React from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ProficiencyUpsertPopup from './ProficiencyUpsertPopup';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { I18N } from '../I18N';
import { IfTrue } from '../Helpers';
import { UpsertAction } from '../Enums';
import IconButtonEx from '../components/IconButtonEx';
import { ProfileGraph } from '../apiClients/ProfileClient';
import { ProficiencySelection, TemplateRevision2ProficiencyGroupDisplay, WeightSelection } from '../models/dtos';
import useViewportMode from '../hooks/useViewportMode';
import { MobileEntityCard, ResponsiveCollection } from '../components/responsive';

export interface ProficiencyGraphDisplay {
    ProficiencyId: string;
    ProficiencyGroupId: string;
    Type: string;
    Name: string;
    WeightEnum: WeightSelection;
    Selection: ProficiencySelection;

}

interface ProficiencyGraphsGridProps {
    profileGraph: ProfileGraph;
    selectedProficiencyGroup: TemplateRevision2ProficiencyGroupDisplay;
    mode: UpsertAction;
    loadData: () => Promise<void>;
    handleSetWeight: (proficiencyId: string, value: string) => Promise<void>;
    handleSelectionChanged: (ap: any, value: string) => void; // Replace 'any' with the actual type of ap
    handleRemove: (ap: any) => void; // Replace 'any' with the actual type of ap
    abilityLevel: string[];
    behaviorLevel: string[];
    weightSelections: string[];
    showReadOnlyLegendButtons?: boolean;
}

interface RatingScaleLegendProps {
    label: string;
    levels: string[];
    helperText?: string;
}

const RatingScaleLegend = ({ label, levels, helperText }: RatingScaleLegendProps) => {
    const [open, setOpen] = React.useState(false);
    const titleId = `${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-title`;

    return <>
        <Button
            size="small"
            variant="outlined"
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={() => setOpen(true)}>
            View {label.toLowerCase()}
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby={titleId} maxWidth="xs" fullWidth>
            <DialogTitle id={titleId}>{label}</DialogTitle>
            <DialogContent dividers>
                <List component="ol" aria-label={`${label} levels`} sx={{ py: 0 }}>
                    {levels.map((level, index) => <ListItem component="li" key={level}>
                        <Typography><strong>{index + 1}.</strong> {level}</Typography>
                    </ListItem>)}
                </List>
                {helperText && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{helperText}</Typography>}
            </DialogContent>
            <DialogActions><Button autoFocus onClick={() => setOpen(false)}>Close</Button></DialogActions>
        </Dialog>
    </>;
};

const readOnlyTypeLabel = (ap: any) => {
    if (ap.Type !== 'Behavior')
        return 'Ability';

    return ap.IsNegative
        ? 'Counterproductive behavior — lower frequency is better'
        : 'Positive behavior';
};

const ProficiencyGraphsGrid: React.FC<ProficiencyGraphsGridProps> = ({ profileGraph, selectedProficiencyGroup, mode, loadData, handleSetWeight, handleSelectionChanged, handleRemove, abilityLevel, behaviorLevel, weightSelections, showReadOnlyLegendButtons = true }) => {
    const { mode: viewportMode } = useViewportMode();
    const readOnly = mode === UpsertAction.View;
    const rows = profileGraph.Proficiencies
        .filter(f => f.ProficiencyGroupId === selectedProficiencyGroup.ProficiencyGroupId)
        .map(x => ({ ...x, id: x.ProficiencyId }));

    const selectionControl = (ap: any) => readOnly
        ? <Typography aria-label={`Selection for ${ap.Name}`}>{ap?.Selection?.SelectionText || 'Not selected'}</Typography>
        : <Select
            size="small"
            fullWidth
            value={ap?.Selection?.SelectionText ?? ''}
            inputProps={{ 'aria-label': `Selection for ${ap.Name}` }}
            onChange={(event) => handleSelectionChanged(ap, event.target.value)}
            sx={{ minHeight: 44 }}
        >
            {(ap.Type === 'Ability' ? abilityLevel : behaviorLevel)
                .map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
        </Select>;

    const weightControl = (ap: any) => readOnly
        ? <Typography aria-label={`Weight for ${ap.Name}`}>{ap?.Weight || 'Not set'}</Typography>
        : <Select
            size="small"
            fullWidth
            value={ap?.Weight ?? ''}
            inputProps={{ 'aria-label': `Weight for ${ap.Name}` }}
            onChange={(event) => handleSetWeight(ap.ProficiencyId, event.target.value)}
            sx={{ minHeight: 44 }}
        >
            {weightSelections.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
        </Select>;

    return (
        <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
                data-testid="responsive-proficiency-grid-scroll"
                sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: viewportMode === 'desktop' ? 'hidden' : 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                }}>
            {readOnly && showReadOnlyLegendButtons && <Stack
                component="section"
                aria-label="Read-only rating scale legends"
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ p: viewportMode === 'desktop' ? 0 : 1.5 }}>
                <RatingScaleLegend label="Ability rating scale" levels={abilityLevel} />
                <RatingScaleLegend
                    label="Behavior frequency scale"
                    levels={behaviorLevel}
                    helperText="For counterproductive behavior, lower frequency is better." />
            </Stack>}
            <ResponsiveCollection
            items={rows}
            getKey={row => row.ProficiencyId}
            viewMode={viewportMode}
            ariaLabel={`${selectedProficiencyGroup.ProficiencyGroupName} proficiencies`}
            emptyState={null}
            renderCard={(ap) => (
                <MobileEntityCard
                    title={ap.Name}
                    subtitle={readOnly ? readOnlyTypeLabel(ap) : ap.Type}
                    ariaLabel={`Template proficiency: ${ap.Name}`}
                    fields={[
                        { label: 'Weight', value: weightControl(ap), fullWidth: true },
                        { label: 'Selection', value: selectionControl(ap), fullWidth: true },
                    ]}
                    actions={mode === UpsertAction.Create || mode === UpsertAction.Update ? (
                        <>
                            <ProficiencyUpsertPopup mode={mode} proficiencyId={ap.ProficiencyId} onChange={() => loadData()} hoverContent={`Edit ${ap.Name}`} />
                            <IconButtonEx
                                size='small'
                                hoverContent={`Remove ${ap.Name}`}
                                message={`${I18N.Translate('Are you sure you want to remove')} '${ap.Name}?'`}
                                onClick={() => handleRemove(ap)}>
                                <RemoveCircleOutlineIcon htmlColor='red' fontSize='small' />
                            </IconButtonEx>
                        </>
                    ) : undefined}
                />
            )}
            desktop={<DataGridPro
            sx={{ height: '100%' }}
            density="compact"
            rows={rows}
            columns={[
                ...(mode === UpsertAction.Create || mode === UpsertAction.Update ? [{
                    field: 'Edit',
                    sortable: false,
                    filterable: false,
                    resizable: false,
                    width: 50,
                    align: 'center',
                    disableColumnMenu: true,
                    renderCell: ({ row: ap }) =>
                        <ButtonGroup
                            key={ap.ProficiencyId}>
                            <ProficiencyUpsertPopup mode={mode} proficiencyId={ap.ProficiencyId} onChange={() => loadData()} hoverContent='' />
                        </ButtonGroup>
                }] as GridColumns : []),
                {
                    field: 'Type',
                    headerName: I18N.Translate('Type'),
                    width: readOnly ? 280 : 150,
                    renderCell: readOnly
                        ? ({ row: ap }) => <Typography
                            variant="body2"
                            aria-label={`Criterion type: ${readOnlyTypeLabel(ap)}`}
                            sx={{ whiteSpace: 'normal', lineHeight: 1.25 }}>
                            {readOnlyTypeLabel(ap)}
                        </Typography>
                        : undefined,
                },
                {
                    field: 'Name',
                    headerName: I18N.Translate('Proficiency Name'),
                    flex: 1,
                    minWidth: 220,
                    renderCell: ({ row: ap }) => (
                        <Tooltip title={ap.Name} enterDelay={0}>
                            <Typography
                                tabIndex={0}
                                noWrap
                                aria-label={`Full proficiency name: ${ap.Name}`}
                                sx={{ width: '100%' }}
                            >
                                {ap.Name}
                            </Typography>
                        </Tooltip>
                    )
                },
                {
                    field: 'Weight',
                    headerName: I18N.Translate('Weight'),
                    width: 210,
                    renderCell: ({ row: ap }) =>
                        readOnly ? weightControl(ap) : <Select
                            key={ap.ProficiencyId}
                            size="small"
                            fullWidth={true}
                            value={ap?.Weight ?? ''}
                            onChange={(e) => handleSetWeight(ap.ProficiencyId, e.target.value)}  >
                            {weightSelections.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                        </Select>
                },
                {
                    field: 'Selection',
                    headerName: I18N.Translate('Selection'),
                    width: 165,
                    renderCell: ({ row: ap }) =>
                        <>
                            {IfTrue(ap.Type === 'Ability', () =>
                                readOnly ? selectionControl(ap) : <Select
                                    key={ap.ProficiencyId}
                                    size="small"
                                    fullWidth={true}
                                    value={ap?.Selection?.SelectionText ?? ''}
                                    onChange={(e) => handleSelectionChanged(ap, e.target.value)}  >
                                    {abilityLevel.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                                </Select>
                            )}
                            {IfTrue(ap.Type === 'Behavior', () =>
                                readOnly ? selectionControl(ap) : <Select
                                    key={ap.ProficiencyId}
                                    size="small"
                                    fullWidth={true}
                                    value={ap?.Selection?.SelectionText ?? ''}
                                    onChange={(e) => handleSelectionChanged(ap, e.target.value)} >
                                    {behaviorLevel.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
                                </Select>
                            )}
                        </>
                },
                ...(mode === UpsertAction.Create || mode === UpsertAction.Update ? [{
                    field: 'Remove',
                    sortable: false,
                    filterable: false,
                    resizable: false,
                    disableColumnMenu: true,
                    width: 75,
                    align: 'center',
                    hide: false,
                    renderCell: ({ row: ap }) =>
                        <IconButtonEx
                            key={ap.ProficiencyId}
                            size='small'
                            message={`${I18N.Translate('Are you sure you want to remove')} '${ap.Name}?'`}
                            onClick={() => handleRemove(ap)}>
                            <RemoveCircleOutlineIcon htmlColor='red' fontSize='small' />
                        </IconButtonEx>
                }] as GridColumns : []),
            ]}
            components={{
                Toolbar: GridToolbar
            }}
        />}
        />
            </Box>
        </Box>
    );
};

export default ProficiencyGraphsGrid;
